import React, {
	useState,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import {
	Box,
	Button,
	TextField,
	MenuItem,
	Typography,
	InputAdornment,
} from '@mui/material';
import { addDays } from 'date-fns';
import Modal from './Modal';
import useConfirm from '../useConfirm';
import StripeFeeQuoteHelper from '../StripeFeeQuoteHelper';
import BidFormatToggle from '../LineItems/BidFormatToggle';
import LineItemsEditor from '../LineItems/LineItemsEditor';
import { makeApiRequest } from '../../Shared/Api';
import { refreshOnboardingChecklist } from '../../Shared/onboarding';
import { useSnackbar } from 'notistack';
import { JobDetailContext } from '../../Pages/Jobs/context/JobDetailContext';
import { useStripePaymentsEnabled } from '../../Context/AppContext';
import {
	createDraftLineItem,
	itemizedBidAmountMatches,
	lineItemsGrandTotal,
	resolveChangeOrderItemized,
	serializeLineItemsForApi,
} from '../../Shared/lineItems';
import { calculateGrossUpAmount, formatCurrency } from '../../Shared/stripeFee';
import { toDatetimeLocalValue } from '../../Shared/datetime';
import {
	BID_REASONS,
	bidHasActiveCheckoutLink,
	getCheckoutExpireConfirmOptions,
} from '../../Shared/bids';

const ChangeOrderModal = ({ open, onClose, jobId, onJobUpdated }) => {
	const [amount, setAmount] = useState('');
	const [useItemizedBid, setUseItemizedBid] = useState(false);
	const [quoteIncludesStripeFee, setQuoteIncludesStripeFee] = useState(false);
	const [expiresAtLocal, setExpiresAtLocal] = useState('');
	const [depositPercent, setDepositPercent] = useState('');
	const [reason, setReason] = useState('additional_work');
	const [loading, setLoading] = useState(false);
	const [lineItemsDraft, setLineItemsDraft] = useState([]);
	const [initialLineItems, setInitialLineItems] = useState([]);
	const [editorSeed, setEditorSeed] = useState(0);
	const lineItemsTableRef = useRef(null);
	const { enqueueSnackbar } = useSnackbar();
	const { confirm, confirmDialog } = useConfirm();
	const { state } = useContext(JobDetailContext);
	const { selectedJob } = state;
	const stripePaymentsEnabled = useStripePaymentsEnabled();
	const showingItemized = useItemizedBid;

	const lineItemsTotal = useMemo(
		() => lineItemsGrandTotal(lineItemsDraft),
		[lineItemsDraft],
	);
	const quotedTotal = useMemo(() => {
		if (!showingItemized || !quoteIncludesStripeFee || !stripePaymentsEnabled) {
			return lineItemsTotal;
		}
		return calculateGrossUpAmount(lineItemsTotal);
	}, [
		showingItemized,
		lineItemsTotal,
		quoteIncludesStripeFee,
		stripePaymentsEnabled,
	]);

	useEffect(() => {
		if (!open) return;

		setReason('additional_work');
		setQuoteIncludesStripeFee(
			Boolean(selectedJob?.bid_details?.quote_includes_stripe_fee),
		);

		const defaultExpiry = toDatetimeLocalValue(addDays(new Date(), 14));
		setExpiresAtLocal(
			selectedJob?.bid_details?.expires_at
				? toDatetimeLocalValue(new Date(selectedJob.bid_details.expires_at))
				: defaultExpiry,
		);

		setUseItemizedBid(resolveChangeOrderItemized(selectedJob));
		setAmount(
			Number(selectedJob?.bid_details?.amount ?? selectedJob?.bid_amount) > 0
				? String(selectedJob.bid_details?.amount ?? selectedJob.bid_amount)
				: '',
		);
		setDepositPercent(
			selectedJob?.bid_details?.deposit_percent
				? String(selectedJob.bid_details.deposit_percent)
				: '',
		);

		const prior = selectedJob?.bid_details?.line_items || [];
		setInitialLineItems(
			prior.length
				? prior.map((item) => ({
						...createDraftLineItem(),
						description: item.description || '',
						quantity: item.quantity || 1,
						unit_price: item.unit_price ?? '',
					}))
				: [createDraftLineItem()],
		);
		setLineItemsDraft([]);
		setEditorSeed((n) => n + 1);
	}, [open, selectedJob]);

	const confirmCheckoutExpiry = () => {
		const { message, title, confirmText, confirmColor } =
			getCheckoutExpireConfirmOptions('changeOrder');
		return confirm(message, { title, confirmText, confirmColor });
	};

	const submitChangeOrder = async ({
		confirmExpireCheckout = false,
		serializedLineItems = [],
	} = {}) => {
		setLoading(true);
		try {
			const payload = {
				job: jobId,
				reason,
				send_bid: true,
				expires_at: new Date(expiresAtLocal).toISOString(),
				quote_includes_stripe_fee: false,
			};

			if (showingItemized) {
				payload.itemized_pricing = true;
				payload.line_items = serializedLineItems;
			} else {
				payload.amount = parseFloat(amount);
				payload.itemized_pricing = false;
			}
			if (stripePaymentsEnabled) {
				payload.quote_includes_stripe_fee = quoteIncludesStripeFee;
			}
			if (depositPercent) {
				payload.deposit_percent = Number(depositPercent);
			}
			if (confirmExpireCheckout) {
				payload.confirm_expire_checkout = true;
			}

			await makeApiRequest('POST', '/bids/', payload);

			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}

			refreshOnboardingChecklist();
			enqueueSnackbar('Change order sent to customer', { variant: 'success' });
			onClose();
		} catch (error) {
			const data = error.data || {};
			if (
				data.requires_checkout_expiry_confirmation &&
				!confirmExpireCheckout
			) {
				const confirmed = await confirmCheckoutExpiry();
				if (confirmed) {
					setLoading(false);
					await submitChangeOrder({
						confirmExpireCheckout: true,
						serializedLineItems,
					});
				}
				return;
			}
			enqueueSnackbar(error.message || 'Failed to send change order', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async () => {
		let serializedLineItems = [];
		if (showingItemized) {
			const flushed =
				(await lineItemsTableRef.current?.flushPendingSaves?.()) ||
				lineItemsDraft;
			serializedLineItems = serializeLineItemsForApi(flushed);
			const total = lineItemsGrandTotal(serializedLineItems);
			if (!serializedLineItems.length || total <= 0) {
				enqueueSnackbar(
					'Add at least one line item with a price before sending a change order.',
					{ variant: 'error' },
				);
				return;
			}
		} else if (
			!amount ||
			Number.isNaN(Number(amount)) ||
			parseFloat(amount) <= 0
		) {
			enqueueSnackbar('Please enter a valid amount', { variant: 'error' });
			return;
		}

		if (!expiresAtLocal) {
			enqueueSnackbar('Choose a bid expiration date', { variant: 'warning' });
			return;
		}

		if (bidHasActiveCheckoutLink(selectedJob?.bid_details)) {
			const confirmed = await confirmCheckoutExpiry();
			if (!confirmed) return;
			await submitChangeOrder({
				confirmExpireCheckout: true,
				serializedLineItems,
			});
			return;
		}

		await submitChangeOrder({ serializedLineItems });
	};

	const previousBidTotal = selectedJob?.bid_details?.amount;
	const bidTotalChanged =
		showingItemized &&
		previousBidTotal != null &&
		!itemizedBidAmountMatches(
			lineItemsTotal,
			previousBidTotal,
			quoteIncludesStripeFee,
		);

	return (
		<>
			<Modal
				open={open}
				onClose={onClose}
				modalTitle="Create Change Order"
				maxWidth={showingItemized ? 'md' : 'sm'}
				alwaysMaxWidth
				useActionButtons
				actionButtons={
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button onClick={onClose} disabled={loading}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleSubmit}
							disabled={loading}
						>
							{loading ? 'Sending...' : 'Send Change Order'}
						</Button>
					</Box>
				}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
					<Typography variant="body2" color="text.secondary">
						Send a revised proposal when the scope or price has changed. This
						withdraws the previous open bid and emails the customer an updated
						PDF.
					</Typography>

					<BidFormatToggle
						value={useItemizedBid}
						onChange={setUseItemizedBid}
						disabled={loading}
					/>

					{showingItemized ? (
						<Box>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								Edit this change order&apos;s line items below. Starts from the
								previous bid when available.
							</Typography>
							<LineItemsEditor
								key={`change-order-items-${editorSeed}`}
								ref={lineItemsTableRef}
								initialItems={initialLineItems}
								onItemsChange={setLineItemsDraft}
								compact
								quoteIncludesStripeFee={
									stripePaymentsEnabled && quoteIncludesStripeFee
								}
							/>
							{bidTotalChanged && lineItemsDraft.length > 0 ? (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ mt: 1, display: 'block' }}
								>
									Previous bid: ${formatCurrency(previousBidTotal)} → new total:{' '}
									${formatCurrency(quotedTotal)}
								</Typography>
							) : null}
							{stripePaymentsEnabled ? (
								<Box sx={{ mt: 2 }}>
									<StripeFeeQuoteHelper
										lineItems={lineItemsDraft}
										includesFee={quoteIncludesStripeFee}
										disabled={loading}
										onApplyFee={() => {}}
										onIncludesFeeChange={setQuoteIncludesStripeFee}
									/>
								</Box>
							) : null}
						</Box>
					) : (
						<>
							<TextField
								label="New Bid Amount"
								fullWidth
								required
								type="text"
								inputMode="decimal"
								value={amount}
								onChange={(e) => setAmount(e.target.value)}
								onBlur={(e) => {
									const parsed = parseFloat(e.target.value);
									if (!Number.isNaN(parsed) && parsed > 0) {
										setAmount(parsed.toFixed(2));
									}
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">$</InputAdornment>
									),
								}}
								helperText="One project total on the customer proposal PDF."
							/>

							{stripePaymentsEnabled ? (
								<StripeFeeQuoteHelper
									amount={amount}
									includesFee={quoteIncludesStripeFee}
									disabled={loading}
									onApplyFee={(grossAmount) => {
										setAmount(Number(grossAmount).toFixed(2));
										setQuoteIncludesStripeFee(true);
									}}
									onIncludesFeeChange={setQuoteIncludesStripeFee}
								/>
							) : null}
						</>
					)}

					<TextField
						label="Proposal valid until"
						type="datetime-local"
						fullWidth
						required
						value={expiresAtLocal}
						onChange={(e) => setExpiresAtLocal(e.target.value)}
						slotProps={{ inputLabel: { shrink: true } }}
						helperText="The customer can accept or decline until this date."
					/>

					<TextField
						label="Deposit required on acceptance"
						type="number"
						fullWidth
						value={depositPercent}
						onChange={(e) => setDepositPercent(e.target.value)}
						disabled={loading}
						slotProps={{
							input: {
								endAdornment: <InputAdornment position="end">%</InputAdornment>,
								inputProps: { min: 1, max: 100, step: 1 },
							},
						}}
						helperText="Optional. Prior deposits on this job count toward the new total."
					/>

					<TextField
						select
						label="Reason for change"
						fullWidth
						required
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					>
						{Object.entries(BID_REASONS).map(([value, label]) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</TextField>
				</Box>
			</Modal>

			{confirmDialog}
		</>
	);
};

export default ChangeOrderModal;
